// Gravador da resposta da visita — o microfone do próprio celular.
//
// O iPhone (Safari) grava "audio/mp4" (AAC); o Android (Chrome), "audio/webm"
// (Opus). Os dois o transcritor aceita. Pede-se o primeiro que o aparelho
// souber gravar; se nenhum da lista, o navegador escolhe.
//
// Microfone só abre com HTTPS e com a permissão da pessoa — a primeira vez, o
// celular pergunta. E ao parar, o microfone é DEVOLVIDO: senão o iPhone segue
// mostrando o aviso de gravação e o próximo "Gravar" falha.

const PREFERIDOS = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus'];

export function escolherFormato(suporta: (tipo: string) => boolean): string | undefined {
  return PREFERIDOS.find((t) => { try { return suporta(t); } catch { return false; } });
}

/** 65 -> "1:05". */
export const formatarDuracao = (seg: number): string =>
  `${Math.floor(seg / 60)}:${String(Math.floor(seg % 60)).padStart(2, '0')}`;

export const gravacaoDisponivel = (): boolean =>
  typeof window !== 'undefined' && 'MediaRecorder' in window && Boolean(navigator.mediaDevices?.getUserMedia);

export interface AudioGravado { blob: Blob; tipoMime: string; duracaoSeg: number; gravadoEm: string }

export class Gravador {
  private gravador: MediaRecorder | null = null;
  private fluxo: MediaStream | null = null;
  private pedacos: Blob[] = [];
  private inicio = 0;

  get gravando(): boolean { return this.gravador?.state === 'recording'; }
  get iniciadoEm(): number { return this.inicio; }

  async iniciar(): Promise<void> {
    if (this.gravando) return;
    this.fluxo = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true } });
    const formato = escolherFormato((t) => MediaRecorder.isTypeSupported(t));
    this.gravador = new MediaRecorder(this.fluxo, formato ? { mimeType: formato } : undefined);
    this.pedacos = [];
    this.gravador.ondataavailable = (e) => { if (e.data.size > 0) this.pedacos.push(e.data); };
    // Pedaços de 1 s: se o celular travar no meio, o que já foi dito não se perde todo.
    this.gravador.start(1000);
    this.inicio = Date.now();
  }

  parar(): Promise<AudioGravado | null> {
    const g = this.gravador;
    if (!g || g.state === 'inactive') { this.soltar(); return Promise.resolve(null); }
    return new Promise((ok) => {
      g.onstop = () => {
        const tipoMime = (g.mimeType || this.pedacos[0]?.type || 'audio/mp4').split(';')[0]!;
        const blob = new Blob(this.pedacos, { type: tipoMime });
        const duracaoSeg = Math.max(1, Math.round((Date.now() - this.inicio) / 1000));
        this.soltar();
        ok(blob.size > 0 ? { blob, tipoMime, duracaoSeg, gravadoEm: new Date(this.inicio).toISOString() } : null);
      };
      g.stop();
    });
  }

  /** Devolve o microfone ao sistema. */
  private soltar(): void {
    this.fluxo?.getTracks().forEach((t) => t.stop());
    this.fluxo = null;
    this.gravador = null;
  }
}
